/**
 * APPLICATION CONSTANTS
 * Central repository for all application-wide constants
 */

// ============================================================================
// API CONFIGURATION
// ============================================================================

const DEFAULT_HOST = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
const DEFAULT_PROTOCOL = typeof window !== 'undefined' ? window.location.protocol : 'http:';
const DEFAULT_API_PORT = 52051; // backend port exposed by the gateway on host in local dev
const DEFAULT_CANDLE_CONSTRUCTOR_PORT = 50004;
const DEFAULT_WS_PROTOCOL = DEFAULT_PROTOCOL === 'https:' ? 'wss:' : 'ws:';
// Treat any standard Vite/dev-server port as a proxy environment so relative paths are used
const USING_NGINX_PROXY =
  typeof window !== 'undefined' &&
  (window.location.port === '' || window.location.port === '80' || window.location.port === '3001' || window.location.port === '8520' || window.location.port === '5173' || window.location.port === '3000');

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (USING_NGINX_PROXY ? '' : `${DEFAULT_PROTOCOL}//${DEFAULT_HOST}:${DEFAULT_API_PORT}`);

export const WS_BASE_URL =
  import.meta.env.VITE_WS_BASE_URL ||
  (USING_NGINX_PROXY ? '/ws' : `${DEFAULT_WS_PROTOCOL}//${DEFAULT_HOST}:${DEFAULT_API_PORT}`);

export const CANDLE_CONSTRUCTOR_URL =
  import.meta.env.VITE_CANDLE_CONSTRUCTOR_URL ||
  (USING_NGINX_PROXY ? '/svc/candles' : `${DEFAULT_PROTOCOL}//${DEFAULT_HOST}:${DEFAULT_CANDLE_CONSTRUCTOR_PORT}`);

export const MARKET_INGESTION_URL =
  import.meta.env.VITE_MARKET_INGESTION_URL ||
  // '/svc/ingestion' had no proxy (vite or nginx); market-ingestion is served at /svc/market-ingestion.
  (USING_NGINX_PROXY ? '/svc/market-ingestion' : `${DEFAULT_PROTOCOL}//${DEFAULT_HOST}:50003`);
export const API_TIMEOUT = 30000; // 30 seconds
// The API gateway (SSO entry point) serves everything under /api/v2. Config-based
// endpoints previously used v1, which 404s at the gateway — align to v2 so login and
// all gateway-proxied calls resolve and carry the auth token.
export const API_VERSION = 'v2';
export const SHAPE_ENGINE_URL =
  import.meta.env.VITE_SHAPE_ENGINE_URL ||
  (USING_NGINX_PROXY ? '/svc/shapes' : `${DEFAULT_PROTOCOL}//${DEFAULT_HOST}:50007`);

export const BRAIN_MONITOR_URL =
  import.meta.env.VITE_BRAIN_MONITOR_URL ||
  (USING_NGINX_PROXY ? '/svc/brain-monitor' : `${DEFAULT_PROTOCOL}//${DEFAULT_HOST}:50023`);

export const KNOWLEDGE_GRAPH_URL =
  import.meta.env.VITE_KNOWLEDGE_GRAPH_URL ||
  (USING_NGINX_PROXY ? '/svc/kg' : `${DEFAULT_PROTOCOL}//${DEFAULT_HOST}:50017`);

export const LEARNING_ENGINE_URL =
  import.meta.env.VITE_LEARNING_ENGINE_URL ||
  (USING_NGINX_PROXY ? '/svc/learning' : `${DEFAULT_PROTOCOL}//${DEFAULT_HOST}:50013`);

export const CORE_BRAIN_URL =
  import.meta.env.VITE_CORE_BRAIN_URL ||
  (USING_NGINX_PROXY ? '/svc/core-brain' : `${DEFAULT_PROTOCOL}//${DEFAULT_HOST}:50018`);

// ============================================================================
// SERVICE PORTS
// ============================================================================

export const SERVICE_PORTS = {
  FRONTEND: 52000,
  INGESTION: 52001,
  PERCEPTION: 50005,
  MEANING: 52003,
  LEARNING: 50006,
  EXPLANATION: 52005,
  LANGUAGE: 50019,
  CYBER_GUARD: 50009,
  ACCESS_CONTROL: 50010,
  FILE_SCANNER: 50017,
  SHAPE_RECOGNITION: 50007,
  PATTERN_GENOME: 50012,
  SIGNAL_ENGINE: 50011,
  MULTI_TF_CONSENSUS: 52013,
  EXECUTION: 52014,
  SEMANTIC_MEMORY: 50015,
  BOOK_READER: 50014,
  CONVERSATION: 50013,
  DREAM_ENGINE: 52018,
  KPI_TRACKER: 50020,
  API_MARKETPLACE: 50001,
  LOGGING: 52021,
  DEMO: 52022,
  API_GATEWAY: 52031,
} as const;

// ============================================================================
// TIMEFRAMES
// ============================================================================

export const TIMEFRAMES = [
  '1S',
  '2S',
  '1M',
  '2M',
  '3M',
  '5M',
  '15M',
  '30M',
  '1H',
  '4H',
  '1D',
  '1W',
] as const;

export const TIMEFRAME_LABELS = {
  '1S': '1 Second',
  '2S': '2 Seconds',
  '1M': '1 Minute',
  '2M': '2 Minutes',
  '3M': '3 Minutes',
  '5M': '5 Minutes',
  '15M': '15 Minutes',
  '30M': '30 Minutes',
  '1H': '1 Hour',
  '4H': '4 Hours',
  '1D': '1 Day',
  '1W': '1 Week',
} as const;

export const TIMEFRAME_DURATIONS = {
  '1S': 1000,
  '2S': 2000,
  '1M': 60000,
  '2M': 120000,
  '3M': 180000,
  '5M': 300000,
  '15M': 900000,
  '30M': 1800000,
  '1H': 3600000,
  '4H': 14400000,
  '1D': 86400000,
  '1W': 604800000,
} as const;

// ============================================================================
// STRUCTURE TYPES
// ============================================================================

export const STRUCTURE_TYPES = [
  'FVG',
  'IFVG',
  'BOS',
  'CHoCH',
  'OrderBlock',
  'BreakerBlock',
  'MitigationBlock',
  'LiquidityVoid',
  'EquilibriumZone',
  'LiquidityGrab',
  'InducementZone',
  'PremiumZone',
  'DiscountZone',
  'SwingHigh',
  'SwingLow',
  'TrendLine',
  'Channel',
  'Triangle',
  'Wedge',
  'HeadAndShoulders',
  'DoubleTop',
  'DoubleBottom',
  'FlagPattern',
  'PennantPattern',
  'CupAndHandle',
  'RisingWedge',
  'FallingWedge',
  'AscendingTriangle',
  'DescendingTriangle',
  'SymmetricalTriangle',
  'RectanglePattern',
] as const;

export const STRUCTURE_DESCRIPTIONS = {
  FVG: 'Fair Value Gap - Imbalance in order flow',
  IFVG: 'Inverse Fair Value Gap',
  BOS: 'Break of Structure - Trend continuation signal',
  CHoCH: 'Change of Character - Potential reversal',
  OrderBlock: 'Zone where large orders accumulated',
  BreakerBlock: 'Failed order block turned support/resistance',
  MitigationBlock: 'Zone requiring price mitigation',
  LiquidityVoid: 'Area with minimal trading activity',
  EquilibriumZone: 'Balance between buyers and sellers',
  LiquidityGrab: 'Stop hunt before trend continuation',
  InducementZone: 'Area designed to trap retail traders',
  PremiumZone: 'Overvalued price region',
  DiscountZone: 'Undervalued price region',
  SwingHigh: 'Local price maximum',
  SwingLow: 'Local price minimum',
  TrendLine: 'Support or resistance diagonal',
  Channel: 'Parallel trendlines',
  Triangle: 'Converging trendlines',
  Wedge: 'Narrowing price pattern',
  HeadAndShoulders: 'Reversal pattern with three peaks',
  DoubleTop: 'Two failed attempts at higher price',
  DoubleBottom: 'Two failed attempts at lower price',
  FlagPattern: 'Consolidation within trend',
  PennantPattern: 'Symmetrical triangle consolidation',
  CupAndHandle: 'Rounded bottom with small retracement',
  RisingWedge: 'Bearish narrowing uptrend',
  FallingWedge: 'Bullish narrowing downtrend',
  AscendingTriangle: 'Bullish pattern with horizontal resistance',
  DescendingTriangle: 'Bearish pattern with horizontal support',
  SymmetricalTriangle: 'Neutral consolidation pattern',
  RectanglePattern: 'Horizontal consolidation',
} as const;

// ============================================================================
// SEMANTIC STATES
// ============================================================================

export const SEMANTIC_STATES = {
  UNDERSTANDING: 'understanding',
  UNCERTAINTY: 'uncertainty',
  CONFLICT: 'conflict',
  CONSOLIDATION: 'consolidation',
  PUNISHMENT: 'punishment',
  REWARD: 'reward',
} as const;

// ============================================================================
// CONFIDENCE LEVELS
// ============================================================================

export const CONFIDENCE_LEVELS = {
  VERY_LOW: { min: 0, max: 0.3, label: 'Very Low', color: '#FF0033' },
  LOW: { min: 0.3, max: 0.6, label: 'Low', color: '#FF6B00' },
  MEDIUM: { min: 0.6, max: 0.8, label: 'Medium', color: '#FFD700' },
  HIGH: { min: 0.8, max: 1.0, label: 'High', color: '#00FF88' },
} as const;

// ============================================================================
// COLORS (MILITARY THEME)
// ============================================================================

export const COLORS = {
  // Background
  BACKGROUND_PRIMARY: '#0A1428',
  BACKGROUND_SECONDARY: '#0F1C35',
  BACKGROUND_TERTIARY: '#14243F',
  
  // Text
  TEXT_PRIMARY: '#FFFFFF',
  TEXT_SECONDARY: '#B8C5D6',
  TEXT_TERTIARY: '#8B9AAF',
  TEXT_DISABLED: '#5A6B82',
  
  // Accent
  ACCENT_CYAN: '#00D9FF',
  ACCENT_ORANGE: '#FF6B00',
  ACCENT_GREEN: '#00FF88',
  ACCENT_RED: '#FF0033',
  ACCENT_YELLOW: '#FFD700',
  ACCENT_PURPLE: '#9D4EDD',
  
  // Semantic States
  STATE_UNDERSTANDING: '#00FF88',
  STATE_UNCERTAINTY: '#FFD700',
  STATE_CONFLICT: '#FF6B00',
  STATE_CONSOLIDATION: '#00D9FF',
  STATE_PUNISHMENT: '#FF0033',
  STATE_REWARD: '#00FF88',
  
  // Status
  STATUS_SUCCESS: '#00FF88',
  STATUS_WARNING: '#FFD700',
  STATUS_ERROR: '#FF0033',
  STATUS_INFO: '#00D9FF',
  
  // Health
  HEALTH_HEALTHY: '#00FF88',
  HEALTH_DEGRADED: '#FFD700',
  HEALTH_CRITICAL: '#FF0033',
  HEALTH_OFFLINE: '#5A6B82',
  
  // Trends
  TREND_BULLISH: '#00FF88',
  TREND_BEARISH: '#FF0033',
  TREND_NEUTRAL: '#8B9AAF',
  
  // Charts — TradingView professional colors (ported from lightweight-charts defaults)
  CHART_GRID: '#14243F',
  CHART_AXIS: '#5A6B82',
  CHART_CANDLE_UP: '#26a69a',     // TradingView teal (iconic green)
  CHART_CANDLE_DOWN: '#ef5350',   // TradingView red
  
  // UI Elements
  BORDER_PRIMARY: '#2A3B54',
  BORDER_SECONDARY: '#1F2E44',
  BORDER_ACCENT: '#00D9FF',
  
  BUTTON_PRIMARY: '#00D9FF',
  BUTTON_SECONDARY: '#2A3B54',
  BUTTON_DANGER: '#FF0033',
  
  INPUT_BACKGROUND: '#14243F',
  INPUT_BORDER: '#2A3B54',
  INPUT_FOCUS: '#00D9FF',
  
  // Overlays
  OVERLAY_BACKGROUND: 'rgba(10, 20, 40, 0.95)',
  OVERLAY_BACKDROP: 'rgba(0, 0, 0, 0.7)',
  
  // Gradients
  GRADIENT_PRIMARY: 'linear-gradient(135deg, #00D9FF 0%, #0A1428 100%)',
  GRADIENT_SECONDARY: 'linear-gradient(135deg, #FF6B00 0%, #0A1428 100%)',
  GRADIENT_SUCCESS: 'linear-gradient(135deg, #00FF88 0%, #0A1428 100%)',
  GRADIENT_DANGER: 'linear-gradient(135deg, #FF0033 0%, #0A1428 100%)',
} as const;

// ============================================================================
// BREAKPOINTS
// ============================================================================

export const BREAKPOINTS = {
  MOBILE: 768,
  TABLET: 1280,
  DESKTOP: 1920,
} as const;

// ============================================================================
// Z-INDEX LAYERS
// ============================================================================

export const Z_INDEX = {
  BACKGROUND: 0,
  CONTENT: 1,
  OVERLAY: 10,
  DROPDOWN: 100,
  MODAL: 1000,
  TOOLTIP: 2000,
  NOTIFICATION: 3000,
} as const;

// ============================================================================
// ANIMATION DURATIONS
// ============================================================================

export const ANIMATION_DURATION = {
  INSTANT: 0,
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
  VERY_SLOW: 1000,
} as const;

// ============================================================================
// LOCAL STORAGE KEYS
// ============================================================================

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'auth_user',
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme',
  LANGUAGE: 'language',
  RECENT_VIEWS: 'recent_views',
  SAVED_FILTERS: 'saved_filters',
} as const;

// ============================================================================
// WEBSOCKET EVENTS
// ============================================================================

export const WS_EVENTS = {
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  ERROR: 'error',
  SEMANTIC_EVENT: 'semantic_event',
  STRUCTURE_DETECTED: 'structure_detected',
  CONSENSUS_UPDATE: 'consensus_update',
  CORRECTION_APPLIED: 'correction_applied',
  SERVICE_HEALTH_UPDATE: 'service_health_update',
  METRIC_UPDATE: 'metric_update',
  LOG_ENTRY: 'log_entry',
  ALERT_TRIGGERED: 'alert_triggered',
  HEARTBEAT: 'heartbeat',
} as const;

// ============================================================================
// ROUTES
// ============================================================================

export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  MULTI_TIMEFRAME: '/multi-timeframe',
  KNOWLEDGE_GRAPH: '/knowledge-graph',
  DREAM_ENGINE: '/dream-engine',
  PERFORMANCE: '/performance',
  ADMIN: '/admin',
  ADMIN_SERVICES: '/admin/services',
  ADMIN_USERS: '/admin/users',
  ADMIN_LOGS: '/admin/logs',
  ADMIN_METRICS: '/admin/metrics',
  ADMIN_ALERTS: '/admin/alerts',
  ADMIN_CONFIG: '/admin/config',
  NOT_FOUND: '*',
} as const;

// ============================================================================
// VOICE COMMANDS
// ============================================================================

export const VOICE_WAKE_WORDS = ['hey aivora', 'aivora'];

export const VOICE_COMMANDS = {
  NAVIGATION: ['show', 'open', 'go to', 'navigate to'],
  EXPLANATION: ['explain', 'why', 'what is', 'tell me about'],
  CORRECTION: ['correct', 'fix', 'that is not', 'actually this is'],
  CONTROL: ['pause', 'resume', 'stop', 'start', 'increase', 'decrease'],
  QUERY: ['find', 'search', 'look for', 'get'],
} as const;

// ============================================================================
// POLLING INTERVALS
// ============================================================================

export const POLLING_INTERVALS = {
  SEMANTIC_STATE: 5000, // 5 seconds
  SERVICE_HEALTH: 10000, // 10 seconds
  METRICS: 30000, // 30 seconds
  LOGS: 5000, // 5 seconds
  ALERTS: 15000, // 15 seconds
} as const;

// ============================================================================
// LIMITS
// ============================================================================

export const LIMITS = {
  MAX_KNOWLEDGE_GRAPH_NODES: 10000,
  MAX_LOG_ENTRIES: 1000,
  MAX_METRIC_DATA_POINTS: 1000,
  MAX_SCENARIOS: 100,
  MAX_EXPLANATION_LENGTH: 5000,
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50 MB
  MAX_CONCURRENT_CORRECTIONS: 10,
} as const;

// ============================================================================
// FEATURE FLAGS
// ============================================================================

export const FEATURES = {
  ENABLE_VOICE: import.meta.env.VITE_ENABLE_VOICE === 'true',
  ENABLE_DEMO: import.meta.env.VITE_ENABLE_DEMO === 'true',
  ENABLE_ANALYTICS: true,
  ENABLE_ERROR_REPORTING: true,
  ENABLE_PERFORMANCE_MONITORING: true,
} as const;

// ============================================================================
// ERROR CODES
// ============================================================================

export const ERROR_CODES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  TIMEOUT: 'TIMEOUT',
  NETWORK_ERROR: 'NETWORK_ERROR',
} as const;

// ============================================================================
// SUCCESS MESSAGES
// ============================================================================

export const SUCCESS_MESSAGES = {
  CORRECTION_APPLIED: 'Correction successfully applied and knowledge graph updated',
  LOGIN_SUCCESS: 'Welcome back! Login successful',
  LOGOUT_SUCCESS: 'Logged out successfully',
  SETTINGS_SAVED: 'Settings saved successfully',
  ALERT_CREATED: 'Alert rule created successfully',
  USER_CREATED: 'User created successfully',
} as const;

// ============================================================================
// ERROR MESSAGES
// ============================================================================

export const ERROR_MESSAGES = {
  GENERIC_ERROR: 'An unexpected error occurred. Please try again.',
  NETWORK_ERROR: 'Network connection lost. Please check your internet connection.',
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  SERVICE_UNAVAILABLE: 'Service temporarily unavailable. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
} as const;
